import { useEffect, type ReactElement } from "react";
import { Datagrid, FunctionField, ReferenceField, TextField, useGetMany, useListContext } from "react-admin";
import { useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useNavigate } from "react-router";
import type { Branch } from "../../providers/branchesProvider";
import type { Instructor } from "../../providers/instructorsProvider";
import { grants } from "../../backoffice.access_control";
import { BooleanField } from "../../fields/BooleanField";
import { renderAddress } from "../../misc/AddressDisplay";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { BranchMobileListItem } from "./BranchMobileListItem";

export function BranchesDataGrid(props: { empty: ReactElement; isActive: boolean }) {
  const { empty, isActive } = props;
  const { isLoading, data, total } = useListContext<Branch>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const rowClick = grants.has("Branch:edit") ? "edit" : false;
  const managerIds =
    isMobile && data
      ? Array.from(
          new Set(
            data.flatMap((branch) =>
              branch.defaultDrivingSchoolManagerId ? [branch.defaultDrivingSchoolManagerId] : [],
            ),
          ),
        )
      : [];
  const {
    data: managers = [],
    isLoading: managersLoading,
    error: managersError,
  } = useGetMany<Instructor>("instructors", { ids: managerIds }, { enabled: managerIds.length > 0 });

  useEffect(() => {
    if (managersError) {
      handleError("Fehler beim Laden der Standard-Fahrschulleiter.", managersError);
    }
  }, [managersError]);

  if (isLoading || (managerIds.length > 0 && managersLoading)) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty;
    }

    const managerNamesById = Object.fromEntries(managers.map((manager) => [manager.id, manager.name]));

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((branch) => (
          <BranchMobileListItem
            key={branch.id}
            branch={branch}
            managerName={
              branch.defaultDrivingSchoolManagerId ? managerNamesById[branch.defaultDrivingSchoolManagerId] : undefined
            }
            onClick={rowClick === false ? undefined : () => navigate(`/branches/${branch.id}`)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid
      rowClick={rowClick}
      bulkActionButtons={false}
      empty={empty}
      sx={isActive ? undefined : { "& tbody .RaDatagrid-row": { backgroundColor: "action.hover" } }}
    >
      <TextField label="Name" source="name" />
      <FunctionField
        label="Adresse"
        source="address"
        render={(branch: Branch) => renderAddress(branch?.address ?? undefined)}
      />
      <BooleanField label="Haupt-Filiale" source="isDefaultBranch" />
      <TextField label="Max. Schüler für Theorie" source="maxStudentsForTheory" />
      <ReferenceField label="Standard-Fahrschulleiter" source="defaultDrivingSchoolManagerId" reference="instructors">
        <TextField source="name" />
      </ReferenceField>
    </Datagrid>
  );
}
